import { useMemo, useState } from 'react';
import { Accordion, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { usePageTitle } from '../lib/usePageTitle';
import {
    helpSections,
    HELP_UPDATED,
    type HelpBlock,
    type HelpLang,
    type HelpSection,
} from '../components/help/helpContent';

/** Flattened searchable text for one block, in the chosen language. */
function blockText(block: HelpBlock, lang: HelpLang): string {
    switch (block.kind) {
        case 'p':
        case 'note':
        case 'warn':
            return block.text[lang];
        case 'steps':
        case 'list':
            return block.items[lang].join(' ');
        case 'download':
            return block.label[lang];
    }
}

/** Title + all body text for one section, lower-cased, for the live search. */
function sectionText(section: HelpSection, lang: HelpLang): string {
    return [section.title[lang], ...section.blocks.map((b) => blockText(b, lang))]
        .join(' ')
        .toLowerCase();
}

function Block({ block, lang }: { block: HelpBlock; lang: HelpLang }) {
    const { t } = useTranslation();
    switch (block.kind) {
        case 'p':
            return <p>{block.text[lang]}</p>;
        case 'steps':
            return (
                <ol className="help-steps">
                    {block.items[lang].map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ol>
            );
        case 'list':
            return (
                <ul className="help-list">
                    {block.items[lang].map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            );
        case 'note':
            return (
                <div className="help-callout help-callout--note">
                    <span className="help-callout__label">{t('help.note')}</span>
                    <span>{block.text[lang]}</span>
                </div>
            );
        case 'warn':
            return (
                <div className="help-callout help-callout--warn">
                    <span className="help-callout__label">{t('help.warning')}</span>
                    <span>{block.text[lang]}</span>
                </div>
            );
        case 'download':
            return (
                <div className="help-download">
                    <a
                        className="btn btn-outline-primary btn-sm"
                        href={block.href}
                        download={block.filename}
                    >
                        {block.label[lang]}
                    </a>
                </div>
            );
    }
}

export function HelpPage() {
    const { t, i18n } = useTranslation();
    usePageTitle(t('help.title'));
    const lang: HelpLang = i18n.language.startsWith('en') ? 'en' : 'nb';

    const [query, setQuery] = useState('');
    // Open the first topic by default; the rest start collapsed to keep the page calm.
    const allIds = useMemo(() => helpSections.map((s) => s.id), []);
    const [openKeys, setOpenKeys] = useState<string[]>(() => helpSections.slice(0, 1).map((s) => s.id));

    const q = query.trim().toLowerCase();
    const matchedIds = useMemo(
        () => (q ? helpSections.filter((s) => sectionText(s, lang).includes(q)).map((s) => s.id) : allIds),
        [q, lang, allIds],
    );
    const matched = new Set(matchedIds);

    // While searching, force every match open; otherwise honour the user's toggles.
    const activeKey = q ? matchedIds : openKeys;
    const allOpen = openKeys.length === allIds.length;

    return (
        <>
            <PageHeading title={t('help.title')} />
            <p className="text-body-secondary" style={{ maxWidth: '46rem' }}>
                {t('help.intro')}
            </p>

            <div className="help-toolbar">
                <Form.Control
                    type="search"
                    className="help-search"
                    placeholder={t('help.search')}
                    aria-label={t('help.search')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {!q && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        onClick={() => setOpenKeys(allOpen ? [] : allIds)}
                    >
                        {allOpen ? t('help.collapseAll') : t('help.expandAll')}
                    </Button>
                )}
            </div>

            {q && matchedIds.length === 0 ? (
                <p className="text-body-secondary">{t('help.noResults')}</p>
            ) : (
                <Accordion
                    alwaysOpen
                    activeKey={activeKey}
                    onSelect={(k) => {
                        if (!q) setOpenKeys((k as string[]) ?? []);
                    }}
                    className="help-accordion"
                >
                    {helpSections.map((section, i) => {
                        if (q && !matched.has(section.id)) return null;
                        return (
                            <Accordion.Item eventKey={section.id} key={section.id}>
                                <Accordion.Header>
                                    <span className="help-acc__num" aria-hidden="true">
                                        {i + 1}
                                    </span>
                                    <span className="help-acc__title">{section.title[lang]}</span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {section.blocks.map((block, bi) => (
                                        <Block key={bi} block={block} lang={lang} />
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <p className="help-updated text-body-secondary">
                {t('help.updated')}: {HELP_UPDATED}
            </p>
        </>
    );
}
